import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Input from '../../../ui-components/base/Input';
import Textarea from '../../../ui-components/base/Textarea';
import SelectDropdown from '../../../ui-components/base/SelectDropdown';
import Button from '../../../ui-components/base/Button';
import saveIcon from '../../../styles/images/save_icon.svg';
import styles from './AddTranslationDrawer.module.scss';

const IMAGE_DESCRIPTION_MAX_LENGTH = 100;

const AddTranslationDrawer = ({
  isOpen,
  onClose,
  onSave,
  isButtonLinkMode,
  hasTitleInBannerInfo = true,
  hasDescriptionInBannerInfo,
  hasSecondaryButtonTextInBannerInfo,
  existingTranslation = null,
  usedLocales = []
}) => {
  // Get market languages from Redux
  const marketLanguagesData = useSelector((state) => state.commonData?.marketLanguages);

  const [formData, setFormData] = useState({
    locale: '',
    title: '',
    description: '',
    primaryButtonText: '',
    secondaryButtonText: '',
    imageDescription: '',
  });

  const [touched, setTouched] = useState({});

  // Initialize form data when editing
  useEffect(() => {
    if (existingTranslation) {
      setFormData({ imageDescription: '', ...existingTranslation });
      // Surface errors immediately: a saved translation can become incomplete
      // (e.g. banner_type switched to button_link now requires button text)
      setTouched({
        title: true,
        primaryButtonText: true,
        imageDescription: true,
      });
    } else {
      setFormData({
        locale: '',
        title: '',
        description: '',
        primaryButtonText: '',
        secondaryButtonText: '',
        imageDescription: '',
      });
      setTouched({});
    }
  }, [existingTranslation]);

  // Get available languages (excluding already used ones)
  const availableLanguages = (marketLanguagesData?.otherLocales || [])
    .filter(loc => !usedLocales.includes(loc.locale))
    .map(loc => ({
      value: loc.locale,
      label: loc.title
    }));

  // Validate form - derived during render so Save never sees stale errors
  const errors = useMemo(() => {
    const newErrors = {};

    // Locale is required
    if (!formData.locale || formData.locale.trim().length === 0) {
      newErrors.locale = 'Language is required';
    }

    // Title is required only when the banner itself has a title
    if (hasTitleInBannerInfo) {
      if (!formData.title || formData.title.trim().length === 0) {
        newErrors.title = 'Title is required';
      } else if (formData.title.length > 45) {
        newErrors.title = 'Title must be 45 characters or less';
      }
    }

    // Description validation - conditional based on Banner Information step
    if (hasDescriptionInBannerInfo) {
      // If description was provided in Banner Info, it is required
      if (!formData.description || formData.description.trim().length === 0) {
        newErrors.description = 'Description is required';
      } else if (formData.description.length > 150) {
        newErrors.description = 'Description must be 150 characters or less';
      }
    } else {
      // If description was NOT provided in Banner Info, make it optional
      if (formData.description && formData.description.length > 150) {
        newErrors.description = 'Description must be 150 characters or less';
      }
    }

    // Button Link mode validation
    if (isButtonLinkMode) {
      // Primary button text is required
      if (!formData.primaryButtonText || formData.primaryButtonText.trim().length === 0) {
        newErrors.primaryButtonText = 'Primary button text is required';
      } else if (formData.primaryButtonText.length > 15) {
        newErrors.primaryButtonText = 'Must be 15 characters or less';
      }

      // Secondary button text is optional but has max length
      if (formData.secondaryButtonText && formData.secondaryButtonText.length > 15) {
        newErrors.secondaryButtonText = 'Must be 15 characters or less';
      }
    }

    // Image description is required for both modes
    if (!formData.imageDescription || formData.imageDescription.trim().length === 0) {
      newErrors.imageDescription = 'Image description is required';
    } else if (formData.imageDescription.length > IMAGE_DESCRIPTION_MAX_LENGTH) {
      newErrors.imageDescription = `Image description must be ${IMAGE_DESCRIPTION_MAX_LENGTH} characters or less`;
    }

    return newErrors;
  }, [formData, isButtonLinkMode, hasTitleInBannerInfo, hasDescriptionInBannerInfo]);

  const handleChange = (field) => (value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSave = () => {
    // Mark all fields as touched
    setTouched({
      locale: true,
      title: true,
      description: true,
      primaryButtonText: true,
      secondaryButtonText: true,
      imageDescription: true,
    });

    // Check if form is valid
    if (Object.keys(errors).length === 0) {
      // Clear fields that are disabled because they have no value in Banner Information
      onSave({
        ...formData,
        title: hasTitleInBannerInfo ? formData.title : '',
        description: hasDescriptionInBannerInfo ? formData.description : '',
        secondaryButtonText: hasSecondaryButtonTextInBannerInfo ? formData.secondaryButtonText : '',
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      locale: '',
      title: '',
      description: '',
      primaryButtonText: '',
      secondaryButtonText: '',
      imageDescription: '',
    });
    setTouched({});
    onClose();
  };

  // When editing, Save stays disabled until the admin changes something
  const isDirty = !existingTranslation || Object.keys(formData).some(
    (field) => (formData[field] || '') !== (existingTranslation[field] || '')
  );

  const isFormValid = Object.keys(errors).length === 0 && !!formData.locale && isDirty;

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div className={styles.overlay} onClick={handleClose} />

      {/* Drawer */}
      <div className={styles.drawer}>
        <div className={styles.drawerContent}>
          {/* Header */}
          <div className={styles.drawerHeader}>
            <h2 className={styles.drawerTitle}>
              {existingTranslation ? 'Edit Language' : 'Add Language'}
            </h2>
            <button className={styles.closeButton} onClick={handleClose} type="button">
              ✕
            </button>
          </div>

          {/* Form */}
          <div className={styles.drawerForm}>
            {/* Language Dropdown */}
            <SelectDropdown
              label="Language"
              options={availableLanguages}
              value={formData.locale}
              onChange={handleChange('locale')}
              name="locale"
              placeholder="Select Language"
              required
              disabled={!!existingTranslation} // Disable when editing
              error={touched.locale && !!errors.locale}
              errorMessage={touched.locale ? errors.locale : ''}
            />

            {/* Title */}
            <Input
              label="Title"
              placeholder="Enter Banner Title"
              value={hasTitleInBannerInfo ? formData.title : ''}
              onChange={handleChange('title')}
              disabled={!hasTitleInBannerInfo}
              name="title"
              maxLength={45}
              charLimit="Max 45 characters"
              required={hasTitleInBannerInfo}
              error={touched.title && !!errors.title}
              errorMessage={touched.title ? errors.title : ''}
            />

            {/* Description */}
            <Textarea
              label="Description"
              placeholder="Enter Banner Description"
              value={hasDescriptionInBannerInfo ? formData.description : ''}
              onChange={handleChange('description')}
              disabled={!hasDescriptionInBannerInfo}
              name="description"
              maxLength={150}
              charLimit="Max 150 characters"
              rows={3}
              // showDragHandle
              required={hasDescriptionInBannerInfo}
              error={touched.description && !!errors.description}
              errorMessage={touched.description ? errors.description : ''}
            />

            {/* Button fields - only in Button Link mode */}
            {isButtonLinkMode && (
              <>
                <Input
                  label="Primary Button text"
                  placeholder="Enter Banner Title"
                  value={formData.primaryButtonText}
                  onChange={handleChange('primaryButtonText')}
                  name="primaryButtonText"
                  maxLength={15}
                  charLimit="Max 15 characters"
                  required
                  error={touched.primaryButtonText && !!errors.primaryButtonText}
                  errorMessage={touched.primaryButtonText ? errors.primaryButtonText : ''}
                />

                <Input
                  label="Secondary Button text"
                  placeholder="Enter Banner Title"
                  value={hasSecondaryButtonTextInBannerInfo ? formData.secondaryButtonText : ''}
                  onChange={handleChange('secondaryButtonText')}
                  disabled={!hasSecondaryButtonTextInBannerInfo}
                  name="secondaryButtonText"
                  maxLength={15}
                  charLimit="Max 15 characters"
                  error={touched.secondaryButtonText && !!errors.secondaryButtonText}
                  errorMessage={touched.secondaryButtonText ? errors.secondaryButtonText : ''}
                />
              </>
            )}

            {/* Image Description - shown for both modes */}
            <Textarea
              label="Image Description/Alt Tag"
              placeholder="Enter Image Description"
              value={formData.imageDescription || ''}
              onChange={handleChange('imageDescription')}
              name="imageDescription"
              maxLength={IMAGE_DESCRIPTION_MAX_LENGTH}
              charLimit={`${(formData.imageDescription || '').length}/${IMAGE_DESCRIPTION_MAX_LENGTH}`}
              rows={3}
              // showDragHandle
              required
              error={touched.imageDescription && !!errors.imageDescription}
              errorMessage={touched.imageDescription ? errors.imageDescription : ''}
            />
          </div>

          {/* Footer */}
          <div className={styles.drawerFooter}>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!isFormValid}
              type="button"
              // iconRight="save"
              fullWidth
              className={styles.saveButton}
            >
              Save
              <span className={styles.saveIcon}>
                <img src={saveIcon} alt="Saved Icon" />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTranslationDrawer;
